// src/lib/config/api.config.ts
// Centralized API endpoint configuration
// All paths verified against openapi.json — gateway-facing (no service prefix)
// All ID params accept string | number — template literals auto-coerce
// © 2025–2026 MCX Services, LLC. All rights reserved.

type ID = string | number

const getBaseURL = (): string => {
    if (import.meta.env.VITE_API_URL) {
        return import.meta.env.VITE_API_URL
    }
    return 'https://api.qxproveit.com'
}

export const API_CONFIG = {
    BASE_URL: getBaseURL(),
    TIMEOUT: Number(import.meta.env.VITE_API_TIMEOUT) || 30000,
    ENV: import.meta.env.VITE_ENV || 'development',

    ENDPOINTS: {
        AUTH: {
            LOGIN: '/api/auth/login',
            SIGNUP: '/api/auth/signup',
            LOGOUT: '/api/auth/logout',
            REFRESH: '/api/auth/refresh',
            ME: '/api/auth/me',
        },

        PROJECTS: {
            LIST: '/api/projects',
            CREATE: '/api/projects',
            GET: (id: ID) => `/api/projects/${id}`,
            UPDATE: (id: ID) => `/api/projects/${id}`,
            DELETE: (id: ID) => `/api/projects/${id}`,
        },

        TEST_SUITES: {
            LIST: '/api/suites',
            CREATE: '/api/suites',
            GET: (id: ID) => `/api/suites/${id}`,
            UPDATE: (id: ID) => `/api/suites/${id}`,
            DELETE: (id: ID) => `/api/suites/${id}`,
        },

        TEST_CASES: {
            LIST: '/api/testcases',
            CREATE: '/api/testcases',
            GET: (id: ID) => `/api/testcases/${id}`,
            UPDATE: (id: ID) => `/api/testcases/${id}`,
            DELETE: (id: ID) => `/api/testcases/${id}`,
            STEPS: (id: ID) => `/api/testcases/${id}/steps`,
            STEP: (testcaseId: ID, stepId: ID) =>
                `/api/testcases/${testcaseId}/steps/${stepId}`,
        },

        TEST_PLANS: {
            LIST: '/api/testplans',
            CREATE: '/api/testplans',
            GET: (id: ID) => `/api/testplans/${id}`,
            UPDATE: (id: ID) => `/api/testplans/${id}`,
            DELETE: (id: ID) => `/api/testplans/${id}`,
            TEST_CASES: (id: ID) => `/api/testplans/${id}/testcases`,
            TESTCASES: (planId: ID) => `/api/testplans/${planId}/testcases`,
            ASSIGN_TESTCASE: (id: ID) => `/api/testplans/${id}/testcases`,
            REMOVE_TESTCASE: (testplanId: ID, assignmentId: ID) =>
                `/api/testplans/${testplanId}/testcases/${assignmentId}`,
            TESTCASE: (planId: ID, tcId: ID) =>
                `/api/testplans/${planId}/testcases/${tcId}`,
        },

        BUILDS: {
            LIST: '/api/builds',
            CREATE: '/api/builds',
            GET: (id: ID) => `/api/builds/${id}`,
            UPDATE: (id: ID) => `/api/builds/${id}`,
            DELETE: (id: ID) => `/api/builds/${id}`,
        },

        PLATFORMS: {
            LIST: '/api/platforms',
            CREATE: '/api/platforms',
            GET: (id: ID) => `/api/platforms/${id}`,
            UPDATE: (id: ID) => `/api/platforms/${id}`,
            DELETE: (id: ID) => `/api/platforms/${id}`,
        },

        EXECUTIONS: {
            LIST: '/api/executions',
            CREATE: '/api/executions',
            GET: (id: ID) => `/api/executions/${id}`,
            UPDATE: (id: ID) => `/api/executions/${id}`,
            DELETE: (id: ID) => `/api/executions/${id}`,
            STEPS: (id: ID) => `/api/executions/${id}/steps`,
            CREATE_STEP: (id: ID) => `/api/executions/${id}/steps`,
            STEP: (execId: ID, stepId: ID) =>
                `/api/executions/${execId}/steps/${stepId}`,
            BUILD_RESULTS: (buildId: ID) => `/api/builds/${buildId}/results`,
            TESTPLAN_RESULTS: (testplanId: ID) =>
                `/api/testplans/${testplanId}/results`,
        },

        TEST_RUNS: {
            LIST: '/api/testruns',
            CREATE: '/api/testruns',
            GET: (id: ID) => `/api/testruns/${id}`,
            UPDATE: (id: ID) => `/api/testruns/${id}`,
            DELETE: (id: ID) => `/api/testruns/${id}`,
            START: (id: ID) => `/api/testruns/${id}/start`,
            COMPLETE: (id: ID) => `/api/testruns/${id}/complete`,
            CANCEL: (id: ID) => `/api/testruns/${id}/cancel`,
            PROGRESS: (id: ID) => `/api/testruns/${id}/progress`,
            EXECUTIONS: (id: ID) => `/api/testruns/${id}/executions`,
            COMPARE: '/api/testruns/compare',
            HISTORY: '/api/testruns/history',
        },

        REQUIREMENTS: {
            LIST: '/api/requirements',
            CREATE: '/api/requirements',
            GET: (id: ID) => `/api/requirements/${id}`,
            UPDATE: (id: ID) => `/api/requirements/${id}`,
            DELETE: (id: ID) => `/api/requirements/${id}`,
            VERSIONS: (id: ID) => `/api/requirements/${id}/versions`,
            VERSION: (reqId: ID, versionId: ID) =>
                `/api/requirements/${reqId}/versions/${versionId}`,
            COVERAGE: (id: ID) => `/api/requirements/${id}/coverage`,
            COVERAGE_ITEM: (reqId: ID, coverageId: ID) =>
                `/api/requirements/${reqId}/coverage/${coverageId}`,
            STATS: '/api/requirements/stats',
            GENERATE: '/api/requirements/generate',
        },

        REQ_SPECS: {
            LIST: '/api/reqspecs',
            CREATE: '/api/reqspecs',
            GET: (id: ID) => `/api/reqspecs/${id}`,
            UPDATE: (id: ID) => `/api/reqspecs/${id}`,
            DELETE: (id: ID) => `/api/reqspecs/${id}`,
        },

        DRDS: {
            LIST: '/api/drds',
            CREATE: '/api/drds',
            GET: (id: ID) => `/api/drds/${id}`,
            UPDATE: (id: ID) => `/api/drds/${id}`,
            DELETE: (id: ID) => `/api/drds/${id}`,
        },

        DEFECTS: {
            LIST: '/api/defects',
            CREATE: '/api/defects',
            GET: (id: ID) => `/api/defects/${id}`,
            UPDATE: (id: ID) => `/api/defects/${id}`,
            DELETE: (id: ID) => `/api/defects/${id}`,
        },

        REPORTS: {
            DASHBOARD: '/api/reports/dashboard',
            EXECUTION_SUMMARY: '/api/reports/execution-summary',
            TESTPLAN: (id: ID) => `/api/reports/plans/${id}/summary`,
            BUILD: (id: ID) => `/api/reports/build/${id}`,
            TESTCASE_HISTORY: (id: ID) => `/api/reports/testcase/${id}/history`,
            TRENDS_EXECUTION: '/api/reports/trends/execution',
            COVERAGE: '/api/reports/coverage',
            DASHBOARD_METRICS: '/api/reports/dashboard-metrics',
            MY_METRICS: '/api/reports/my-metrics',
            MY_EXECUTION_TREND: '/api/reports/my-execution-trend',
            QUALITY_TREND: '/api/reports/quality-trend',
            EXECUTIVE: '/api/reports/executive',
            MANAGEMENT: '/api/reports/management',
            PROJECT_HEALTH: '/api/reports/project-health',
            TESTPLAN_REPORT: (id: ID) => `/api/reports/testplan/${id}`,
        },

        BILLING: {
            HEALTH: '/api/billing/health',
            FEATURES: '/api/billing/features',
            USAGE: '/api/billing/usage',
            USAGE_BY_LICENSE: (licenseId: ID) =>
                `/api/billing/usage/${licenseId}`,
            BILLING_EVENTS: '/api/billing/billing-events',
            BILLING_EVENTS_BY_LICENSE: (licenseId: ID) =>
                `/api/billing/billing-events/${licenseId}`,
        },

        INTEGRATIONS: {
            LIST: '/api/integrations',
            GET: (id: ID) => `/api/integrations/${id}`,
            CREATE: '/api/integrations',
            UPDATE: (id: ID) => `/api/integrations/${id}`,
            DELETE: (id: ID) => `/api/integrations/${id}`,
            VALIDATE: '/api/integrations/validate',
            JIRA: {
                CONNECT: '/api/integrations/jira/connect',
                SYNC: '/api/integrations/jira/sync',
            },
            GITHUB: {
                CONNECT: '/api/integrations/github/connect',
                SYNC: '/api/integrations/github/sync',
            },
            WEBHOOKS: {
                LIST: '/api/webhooks',
                CREATE: '/api/webhooks',
                GET: (id: ID) => `/api/webhooks/${id}`,
                UPDATE: (id: ID) => `/api/webhooks/${id}`,
                DELETE: (id: ID) => `/api/webhooks/${id}`,
            },
        },

        NOTIFICATIONS: {
            LIST: '/api/notifications',
            UNREAD_COUNT: '/api/notifications',
            MARK_READ: (id: ID) => `/api/notifications/${id}/read`,
            MARK_ALL_READ: '/api/notifications/mark-all-read',
        },

        AUDIT: {
            LIST: '/api/audit',
            STATS: '/api/audit/stats',
        },

        FILES: {
            LIST: '/api/files',
            UPLOAD: '/api/files/upload',
            GET: (id: ID) => `/api/files/${id}`,
            DELETE: (id: ID) => `/api/files/${id}`,
            ATTACHMENTS: '/api/attachments',
        },

        ASYNC: {
            CODE_ANALYSIS: '/api/async/code-analysis',
            SECURITY_SCAN: '/api/async/security-scan',
            EXECUTE_TESTS: '/api/async/execute-tests',
        },

        COMPLIANCE_AUDIT: {
            FRAMEWORKS: '/api/compliance-audit/frameworks',
            FRAMEWORK: (id: ID) => `/api/compliance-audit/frameworks/${id}`,
            AUDITS: '/api/compliance-audit/audits',
            AUDIT: (id: ID) => `/api/compliance-audit/audits/${id}`,
            AUDIT_REPORT: (id: ID) => `/api/compliance-audit/audits/${id}/report`,
            FINDING_STATUS: (auditId: ID, findingId: ID) =>
                `/api/compliance-audit/audits/${auditId}/findings/${findingId}/status`,
            FINDING_FALSE_POSITIVE: (auditId: ID, findingId: ID) =>
                `/api/compliance-audit/audits/${auditId}/findings/${findingId}/false-positive`,
            PROJECT_HISTORY: (projectId: ID) =>
                `/api/compliance-audit/projects/${projectId}/history`,
            STATS: '/api/compliance-audit/stats',
            HEALTH: '/api/compliance-audit/health',
        },
    },
} as const